import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CloudArrowUpIcon, SparklesIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { event } from '../utils/analytics';

const API = 'https://cvanalyzerback.osc-fr1.scalingo.io/api/cv/preview';

const SECTORS = [
    { value: 'informatique', label: 'Informatique' },
    { value: 'marketing', label: 'Marketing' },
    { value: 'finance', label: 'Finance' },
    { value: 'ressources_humaines', label: 'Ressources humaines' },
    { value: 'santé', label: 'Santé' },
    { value: 'autre', label: 'Autre' },
];

const PublicCvPreview = () => {
    const [file, setFile] = useState(null);
    const [sector, setSector] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [result, setResult] = useState(null);

    const onFile = (selected) => {
        if (!selected) return;
        if (selected.size > 15 * 1024 * 1024) {
            setError('Fichier trop volumineux (15 Mo max).');
            return;
        }
        setError('');
        setFile(selected);
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!file) {
            setError('Choisissez un PDF ou un Word.');
            return;
        }
        if (!sector) {
            setError('Choisissez un secteur.');
            return;
        }
        setLoading(true);
        setError('');
        setResult(null);
        event({ action: 'public_preview_start', category: 'engagement', label: sector });
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('sector', sector);
            const response = await fetch(API, { method: 'POST', body: formData });
            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(data.error || (response.status === 429
                    ? 'Un aperçu gratuit par jour. Créez un compte pour le rapport complet dès 5 €.'
                    : 'Analyse impossible. Réessayez.'));
            }
            setResult(data);
            event({ action: 'public_preview_done', category: 'engagement', label: String(data.score || '') });
        } catch (err) {
            setError(err.message || 'Erreur réseau.');
        } finally {
            setLoading(false);
        }
    };

    if (result) {
        const suggestions = Array.isArray(result.suggestions) ? result.suggestions : [];
        return (
            <div className="card-surface p-6 sm:p-8" id="apercu">
                <p className="text-sm font-medium text-blue-700 mb-1">Aperçu offert</p>
                <p className="text-4xl font-semibold text-slate-900 mb-2">{result.score}<span className="text-lg text-slate-500">/100</span></p>
                <p className="text-sm text-slate-600 mb-6">3 premiers conseils. Le détail, l&apos;ATS et le CV restructuré s&apos;ouvrent dès 5 €.</p>
                <ol className="space-y-3 mb-8">
                    {suggestions.map((item, index) => (
                        <li key={index} className="text-sm text-slate-700">
                            <span className="font-semibold text-slate-900">{item.titre}</span>
                            {item.description ? <span className="block text-slate-600 mt-0.5">{item.description}</span> : null}
                        </li>
                    ))}
                </ol>
                <Link
                    to="/register?redirect=/subscription"
                    className="btn-primary inline-flex"
                    onClick={() => event({ action: 'public_preview_unlock', category: 'conversion', label: 'register_subscription' })}
                >
                    Débloquer le rapport dès 5 €
                </Link>
                <p className="mt-3 text-xs text-slate-500">Compte en 30 secondes. Après paiement, vous importez le CV pour le rapport IA complet.</p>
            </div>
        );
    }

    return (
        <form onSubmit={submit} className="card-surface p-6 sm:p-8" id="apercu">
            <label className="block cursor-pointer rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 hover:border-blue-400 px-4 py-8 text-center">
                <CloudArrowUpIcon className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                <span className="text-sm font-medium text-slate-800">
                    {file ? file.name : 'Déposez un PDF ou un Word'}
                </span>
                <span className="block text-xs text-slate-500 mt-1">Sans inscription · 1 aperçu par jour</span>
                <input
                    type="file"
                    accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    className="hidden"
                    onChange={(e) => onFile(e.target.files && e.target.files[0])}
                />
            </label>
            <label className="block mt-4 text-sm font-medium text-slate-700">
                Secteur
                <select
                    value={sector}
                    onChange={(e) => setSector(e.target.value)}
                    className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm bg-white"
                >
                    <option value="">Secteur visé</option>
                    {SECTORS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                </select>
            </label>
            {error && (
                <p className="mt-3 text-sm text-red-600 flex items-start gap-2">
                    <ExclamationTriangleIcon className="w-4 h-4 mt-0.5 shrink-0" />
                    {error}
                </p>
            )}
            <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full mt-5 justify-center disabled:opacity-60"
            >
                {loading ? 'Analyse…' : (
                    <span className="inline-flex items-center justify-center gap-2">
                        <SparklesIcon className="w-5 h-5" />
                        Voir mon score
                    </span>
                )}
            </button>
        </form>
    );
};

export default PublicCvPreview;
